import { create } from "zustand";
import { persist } from "zustand/middleware";
import type { UserRole } from "@ckgo2/shared/types";
import { api, setAuthToken } from "@/api/client";
import i18n from "@/i18n";

export interface User {
  id: string;
  email: string;
  name: string;
  // Mirrors the DB users.role enum via the shared UserRole (incl. "sales") so role checks
  // (e.g. ProposalCard money-approve) don't silently exclude valid roles.
  role: UserRole;
}

interface AuthState {
  user: User | null;
  token: string | null;
  isLoading: boolean;
  error: string | null;

  // Actions
  login: (email: string, password: string) => Promise<boolean>;
  logout: () => Promise<void>;
  checkAuth: () => Promise<void>;
  clearError: () => void;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      user: null,
      token: null,
      isLoading: true,
      error: null,

      login: async (email: string, password: string) => {
        set({ isLoading: true, error: null });
        try {
          const response = await api.post<{ data: { user: User; token: string } }>(
            "/api/admin/auth/login",
            { email, password },
          );
          setAuthToken(response.data.token);
          set({ user: response.data.user, token: response.data.token, isLoading: false });
          return true;
        } catch (error: unknown) {
          const message =
            error instanceof Error ? error.message : i18n.t("shared.auth.loginFailed");
          set({ error: message, isLoading: false });
          return false;
        }
      },

      logout: async () => {
        try {
          await api.post("/api/admin/auth/logout", {});
        } catch {
          // Ignore errors during logout
        }
        setAuthToken(null);
        set({ user: null, token: null });
      },

      checkAuth: async () => {
        set({ isLoading: true });
        try {
          const response = await api.get<{ data: { user: User } }>("/api/admin/auth/me");
          set({ user: response.data.user, isLoading: false });
        } catch {
          setAuthToken(null);
          set({ user: null, token: null, isLoading: false });
        }
      },

      clearError: () => set({ error: null }),
    }),
    {
      name: "ckgo2-auth",
      partialize: (state) => ({ user: state.user, token: state.token }),
      onRehydrateStorage: () => (state) => {
        // Sync persisted token to the API client after localStorage rehydration
        if (state?.token) {
          setAuthToken(state.token);
        }
      },
    },
  ),
);
